import { normalizePage, Page, serviceFetch, serviceJson, TASK_SERVICE_BASE_URL } from './mobileParityApi';

export type TaskFilter =
  | 'ACTIVE'
  | 'ALL'
  | 'NEW'
  | 'ACCEPTED'
  | 'ARRIVED'
  | 'NAVIGATING'
  | 'IN_PROGRESS'
  | 'IN_REVIEW'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'EXPIRED';

export const ACTIVE_TASK_STATUSES = new Set<string>([
  'NEW',
  'ACCEPTED',
  'ARRIVED',
  'NAVIGATING',
  'IN_PROGRESS',
  'IN_REVIEW',
]);

const BACKEND_PAGE_SIZE = 50;
const MAX_REQUESTER_TASK_PAGES = 50;

export type TaskHistoryItem = {
  id: string;
  taskType?: string | null;
  title?: string | null;
  description?: string | null;
  status: string;
  tokenAmount: number;
  tipAmount: number;
  currency: string;
  actualDuration?: string | null;
  requesterDuration?: string | null;
  time?: string;
  price?: string;
  distance?: string | null;
  distanceMiles?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  createdAtDisplay?: string;
  updatedAtDisplay?: string;
  helperArrivedAt?: string | null;
  location?: any;
  helper?: any;
  requester?: any;
  mainPhoto?: string | null;
  photos?: any[];
  comments?: any[];
  interestedHelpers?: any[];
  counterOffers?: any[];
  conversationId?: string | null;
  cancel?: any;
  raw?: any;
};

const NO_IMAGE_URL = 'https://placehold.co/300x200?text=No+Image';

const formatDateTime = (value: unknown) => {
  if (!value) return '';
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const numberFrom = (...values: unknown[]) => {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return 0;
};

const normalizeStatus = (status: unknown) => {
  const normalized = String(status || 'NEW')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

  if (
    normalized === 'CANCELED' ||
    normalized.includes('CANCELLED') ||
    normalized.includes('CANCELED')
  ) {
    return 'CANCELLED';
  }

  if (normalized.includes('EXPIRED')) return 'EXPIRED';
  return normalized || 'NEW';
};

const matchesFilter = (task: TaskHistoryItem, filter: TaskFilter) => {
  if (filter === 'ALL') return true;
  if (filter === 'ACTIVE') return ACTIVE_TASK_STATUSES.has(task.status);
  return task.status === filter;
};

const dedupeTasks = (tasks: TaskHistoryItem[]) =>
  Array.from(new Map(tasks.map((task) => [task.id, task])).values());

const taskSortTime = (task: TaskHistoryItem) => {
  const value = task.updatedAt || task.createdAt || task.raw?.updatedAt || task.raw?.createdAt || '';
  const timestamp = Date.parse(String(value));
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortTasksNewestFirst = (tasks: TaskHistoryItem[]) =>
  [...tasks].sort((a, b) => taskSortTime(b) - taskSortTime(a));

const pageFromTasks = (
  tasks: TaskHistoryItem[],
  page: number,
  size: number
): Page<TaskHistoryItem> => {
  const start = page * size;
  const content = tasks.slice(start, start + size);
  const totalPages = Math.max(1, Math.ceil(tasks.length / size));

  return {
    content,
    number: page,
    size,
    totalElements: tasks.length,
    totalPages,
    first: page === 0,
    last: start + size >= tasks.length,
    numberOfElements: content.length,
    empty: content.length === 0,
  };
};

const normalizeTask = (task: any, pageMeta?: any): TaskHistoryItem => {
  const tokenAmount = numberFrom(task?.taskCharges?.tokenAmount, task?.tokenAmount);
  const tipAmount = numberFrom(task?.taskCharges?.tipAmount, task?.tipAmount);
  const currency = String(task?.taskCharges?.currency || task?.currency || 'USD').toUpperCase();
  const status = normalizeStatus(task?.status);

  return {
    id: String(task?.id || ''),
    taskType: task?.taskType ?? null,
    title: task?.title ?? null,
    description: task?.description ?? null,
    status,
    tokenAmount,
    tipAmount,
    currency,
    actualDuration: task?.actualDuration ?? null,
    requesterDuration: task?.requesterDuration ?? null,
    time: task?.actualDuration || '00:00',
    price: `${currency} ${(tokenAmount + tipAmount).toFixed(2)}`,
    distance: task?.distance ?? '',
    distanceMiles: task?.distanceMiles ?? '',
    createdAt: task?.createdAt ?? null,
    updatedAt: task?.updatedAt ?? null,
    createdAtDisplay: formatDateTime(task?.createdAt),
    updatedAtDisplay: formatDateTime(task?.updatedAt),
    helperArrivedAt: task?.helperArrivedAt || pageMeta?.helperArrivedAt || '',
    location: task?.location || null,
    helper: task?.helper || null,
    requester: task?.requester || null,
    mainPhoto: task?.mainPhoto || task?.photos?.[0] || NO_IMAGE_URL,
    photos: Array.isArray(task?.photos) ? task.photos : [],
    comments: Array.isArray(task?.comments) ? task.comments : [],
    interestedHelpers: Array.isArray(task?.interestedHelpers) ? task.interestedHelpers : [],
    counterOffers: Array.isArray(task?.counterOffers) ? task.counterOffers : [],
    conversationId: task?.conversationId ?? null,
    cancel: task?.cancel || null,
    raw: task,
  };
};

const requesterTasksUrl = (
  page: number,
  size: number,
  refreshKey?: number
) => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  if (refreshKey) params.set('_ts', String(refreshKey));
  return `${TASK_SERVICE_BASE_URL}/api/v1/tasks/requester-tasks?${params}`;
};

async function fetchRequesterTasksPage(
  page = 0,
  size = 20,
  refreshKey?: number
): Promise<Page<TaskHistoryItem>> {
  try {
    const raw = await serviceJson<Page<any>>(
      requesterTasksUrl(page, size, refreshKey),
      {
        method: 'GET',
        acceptJson: true,
        json: false,
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      },
      'Failed to fetch requester tasks'
    );
    const normalized = normalizePage(raw);
    return {
      ...normalized,
      content: (normalized.content || []).map((task) => normalizeTask(task, raw)),
    };
  } catch (error: any) {
    if (String(error?.message || '').includes('404')) {
      return { content: [], number: page, size, totalPages: page, last: true, empty: true };
    }
    throw error;
  }
}

async function fetchAllRequesterTasks(refreshKey?: number): Promise<TaskHistoryItem[]> {
  const collected: TaskHistoryItem[] = [];
  let backendPage = 0;
  let last = false;

  while (!last && backendPage < MAX_REQUESTER_TASK_PAGES) {
    const response = await fetchRequesterTasksPage(backendPage, BACKEND_PAGE_SIZE, refreshKey);
    collected.push(...response.content);
    last = response.last ?? (response.content.length < BACKEND_PAGE_SIZE);
    backendPage += 1;

    if (!response.content.length) break;
  }

  return sortTasksNewestFirst(dedupeTasks(collected));
}

export async function fetchRequesterTasks(
  page = 0,
  size = 20,
  filter: TaskFilter = 'ALL',
  options: { forceRefresh?: boolean } = {}
): Promise<Page<TaskHistoryItem>> {
  const refreshKey = options.forceRefresh ? Date.now() : undefined;
  const tasks = await fetchAllRequesterTasks(refreshKey);
  const filtered = filter === 'ALL' ? tasks : tasks.filter((task) => matchesFilter(task, filter));

  return pageFromTasks(filtered, page, size);
}

export async function fetchTaskById(taskId: string): Promise<TaskHistoryItem> {
  const params = new URLSearchParams({ _ts: String(Date.now()) });
  const raw = await serviceJson<any>(
    `${TASK_SERVICE_BASE_URL}/api/v1/tasks/${encodeURIComponent(taskId)}?${params}`,
    {
      method: 'GET',
      acceptJson: true,
      json: false,
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    },
    'Failed to fetch task details'
  );
  return normalizeTask(raw);
}

export async function resubmitTask(taskId: string): Promise<void> {
  await serviceFetch(
    `${TASK_SERVICE_BASE_URL}/api/v1/tasks/${encodeURIComponent(taskId)}/resubmit`,
    { method: 'POST', json: false },
    'Failed to resubmit task'
  );
}
